import { createContext, useContext } from 'react'
import type { SessionUser } from '@domain/auth'
import type { SignInResult } from '@domain/ports/auth-gateway'
import type { Session } from './session-state'

/**
 * O contexto de sessão. Em módulo próprio, sem componente junto: um arquivo
 * que exporta componente **e** outra coisa quebra o Fast Refresh, e o
 * `react-refresh/only-export-components` do `eslint.config.js` reprova.
 */

export type AuthContextValue = {
  session: Session
  signIn: () => Promise<SignInResult>
  /** Exposto porque a tela de Ajustes (Task 9) sai por aqui, e não navegando
   * para a rota de login à mão — dois caminhos para o mesmo estado divergem. */
  signOut: () => Promise<void>
  /** Nova tentativa do bootstrap, para a falha que se cura (decisão 57). */
  retryBootstrap: () => void
}

export const AuthContext = createContext<AuthContextValue | null>(null)

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext)
  if (value === null) {
    throw new Error('useAuth: falta um <AuthProvider> acima na árvore.')
  }
  return value
}

/**
 * A pessoa autenticada **com a casa preparada**.
 *
 * `Session` é união de cinco estados e só um deles tem app na tela: quem monta
 * as telas de dentro é o `RequireAuth`, e só no `ready`. Essa é uma garantia de
 * composição que o tipo não enxerga, e sem este atalho toda tela abriria um
 * ramo para quatro estados que ela nunca vê — quatro ramos que nenhum teste
 * alcança.
 *
 * Lançar é a tradução da garantia: componente do app montado fora do `ready` é
 * defeito de composição, não estado de tela.
 */
export function useReadyUser(): SessionUser {
  const { session } = useAuth()
  if (session.status !== 'ready') {
    throw new Error(
      `useReadyUser: sessão em "${session.status}". Só o RequireAuth monta o app, e só em "ready".`,
    )
  }
  return session.user
}
